import { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Loader2, PlugZap, Save } from "lucide-react";
import { inputCls } from "./SectionEditor";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const FIELDS = [
  ["smtp_host", "SMTP Host", "smtp.yourprovider.com"],
  ["smtp_port", "SMTP Port", "587"],
  ["smtp_user", "SMTP Username", "you@yourdomain.com"],
  ["smtp_password", "SMTP Password", "Leave blank to keep existing", "password"],
  ["imap_host", "IMAP Host", "imap.yourprovider.com"],
  ["imap_port", "IMAP Port", "993"],
  ["imap_user", "IMAP Username", "you@yourdomain.com"],
  ["imap_password", "IMAP Password", "Leave blank to keep existing", "password"],
  ["notify_email", "Notification Recipient", "team@settld.ai"],
];

export function EmailSettings({ token }) {
  const [settings, setSettings] = useState(null);
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(false);
  const [results, setResults] = useState(null);
  const headers = { Authorization: `Bearer ${token}` };

  useEffect(() => {
    axios
      .get(`${API}/settings`, { headers })
      .then((r) => setResults(null) || setSettings({ notifications_enabled: false, ...r.data.settings }))
      .catch(() => toast.error("Could not load email settings"));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const save = async () => {
    setSaving(true);
    try {
      await axios.put(`${API}/settings`, { settings }, { headers });
      toast.success("Email settings saved.");
    } catch {
      toast.error("Save failed.");
    } finally {
      setSaving(false);
    }
  };

  const test = async () => {
    setTesting(true);
    setResults(null);
    try {
      await save();
      const r = await axios.post(`${API}/settings/test`, {}, { headers });
      setResults(r.data.results);
    } catch {
      toast.error("Connection test failed.");
    } finally {
      setTesting(false);
    }
  };

  if (!settings) {
    return (
      <div className="flex justify-center py-16">
        <Loader2 size={26} className="animate-spin text-navy" />
      </div>
    );
  }

  return (
    <div data-testid="admin-email-settings">
      <h1 className="mb-2 font-display text-2xl font-semibold">Email Settings</h1>
      <p className="mb-6 max-w-xl text-sm leading-relaxed text-slate-500">
        Configure your own mail server for quote-form notifications — nothing is hardcoded. SMTP sends the
        notification when a visitor submits the quote form; IMAP is stored for inbox monitoring. Passwords are
        kept if left blank.
      </p>

      <label className="mb-6 flex items-center gap-3 rounded-2xl border border-slate-200 bg-slate-50 p-4">
        <input
          type="checkbox"
          data-testid="admin-email-enabled"
          checked={!!settings.notifications_enabled}
          onChange={(e) => setSettings({ ...settings, notifications_enabled: e.target.checked })}
          className="h-4 w-4 accent-[#D93A2B]"
        />
        <span className="text-sm font-bold text-ink">Send me an email for every new quote request</span>
      </label>

      <div className="grid gap-4 md:grid-cols-2">
        {FIELDS.map(([key, label, placeholder, type]) => (
          <label key={key} className="block">
            <span className="mb-1 block text-xs font-bold uppercase tracking-[0.1em] text-slate-500">{label}</span>
            <input
              data-testid={`admin-email-${key}`}
              type={type || "text"}
              value={settings[key] || ""}
              placeholder={placeholder}
              onChange={(e) => setSettings({ ...settings, [key]: e.target.value })}
              className={inputCls}
            />
          </label>
        ))}
      </div>

      <div className="mt-7 flex flex-wrap items-center gap-3">
        <button
          data-testid="admin-email-save-btn"
          onClick={save}
          disabled={saving}
          className="flex items-center gap-1.5 rounded-full bg-maple px-5 py-2.5 text-xs font-bold text-white transition-colors hover:bg-[#C23225] disabled:opacity-60"
        >
          {saving ? <Loader2 size={13} className="animate-spin" /> : <Save size={13} />} Save Settings
        </button>
        <button
          data-testid="admin-email-test-btn"
          onClick={test}
          disabled={testing}
          className="flex items-center gap-1.5 rounded-full border border-navy/20 px-5 py-2.5 text-xs font-bold text-navy transition-colors hover:bg-navy hover:text-white disabled:opacity-60"
        >
          {testing ? <Loader2 size={13} className="animate-spin" /> : <PlugZap size={13} />} Save &amp; Test Connection
        </button>
      </div>

      {results && (
        <div data-testid="admin-email-test-results" className="mt-5 space-y-2">
          {Object.entries(results).map(([k, v]) => (
            <div
              key={k}
              className={`rounded-xl border px-4 py-3 text-sm font-semibold ${
                v === "ok" ? "border-aqua-600/30 bg-aqua-600/[0.06] text-aqua-700" : "border-maple/30 bg-maple/[0.06] text-maple"
              }`}
            >
              {k.toUpperCase()}: {v === "ok" ? "Connection successful" : v}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
